import { useEffect, useRef } from 'react';

import { Button } from '@/components/Button/Button';

import styles from './StudentViewBanner.module.css';

interface StudentViewBannerProps {
  canResetComponent: boolean;
  hasHiddenContent: boolean;
  onExit: () => void;
  onResetComponent: () => void;
  onToggleHiddenContent: () => void;
  showHiddenContent: boolean;
}

/**
 * Always-visible reminder that this tab is interacting as the instructor's
 * test student account. Within a component it also offers "Reset test data"
 * (scoped to that one component). When the current page hides content from
 * students (or the reveal is on), a second line offers the display-only "show
 * hidden content" toggle — the actual visibility settings are never changed.
 *
 * It publishes its height as `--student-view-banner-height`, as the app shell
 * publishes the header's: the header sticks below the banner, and the chapter
 * page's contents panel, scroll padding and anchors count both. The header
 * used to slide up under the banner, and the contents panel to cover its logo
 * (#172).
 */
export function StudentViewBanner({
  canResetComponent,
  hasHiddenContent,
  onExit,
  onResetComponent,
  onToggleHiddenContent,
  showHiddenContent,
}: StudentViewBannerProps) {
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const bannerElement = bannerRef.current;

    if (!bannerElement) {
      return undefined;
    }

    const rootStyle = document.documentElement.style;
    const updateBannerHeight = () => {
      rootStyle.setProperty(
        '--student-view-banner-height',
        `${Math.ceil(bannerElement.getBoundingClientRect().height)}px`,
      );
    };

    updateBannerHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateBannerHeight);

      return () => {
        window.removeEventListener('resize', updateBannerHeight);
        rootStyle.removeProperty('--student-view-banner-height');
      };
    }

    const observer = new ResizeObserver(() => {
      updateBannerHeight();
    });

    observer.observe(bannerElement);

    return () => {
      observer.disconnect();
      rootStyle.removeProperty('--student-view-banner-height');
    };
  }, []);

  return (
    <div className={styles.banner} ref={bannerRef} role="status">
      <div className={styles.line}>
        <span className={styles.label}>
          <strong>Student view</strong> — you are interacting as a test
          student. Answers and progress here are saved to your test account
          only.
        </span>
        <div className={styles.lineActions}>
          {canResetComponent ? (
            <Button
              onClick={onResetComponent}
              size="small"
              variant="secondary"
            >
              Reset test data
            </Button>
          ) : null}
          <Button onClick={onExit} size="small" variant="secondary">
            Exit student view
          </Button>
        </div>
      </div>

      {showHiddenContent ? (
        <div className={styles.line} data-tone="reveal">
          <span className={styles.label}>
            Showing content that is <strong>hidden from students</strong> by
            visibility settings. The settings themselves are unchanged.
          </span>
          <Button
            onClick={onToggleHiddenContent}
            size="small"
            variant="secondary"
          >
            Hide hidden content
          </Button>
        </div>
      ) : hasHiddenContent ? (
        <div className={styles.line}>
          <span className={styles.label}>
            Some content is hidden due to visibility settings.
          </span>
          <Button
            onClick={onToggleHiddenContent}
            size="small"
            variant="secondary"
          >
            Show all content
          </Button>
        </div>
      ) : null}
    </div>
  );
}
